<template>
  <div class="reference-filters">
    <reference-filters-header @clear-all="resetFilterInput" />
    <div class="scroll-view">
      <reference-tag-filter
        :selected-tag-ids.sync="hasTagModel"
        :available-tags="availableTags"
      />
      <reference-filter-multiple-text
        label="Keywords"
        :values.sync="hasKeywordModel"
      />
      <reference-filter-multiple-text
        label="Authors"
        :values.sync="hasAuthorModel"
      />
      <reference-filter-value-range
        label="Year"
        min-placeholder="From"
        max-placeholder="To"
        :min-value.sync="publishedAfterYearModel"
        :max-value.sync="publishedBeforeYearModel"
      />
      <reference-filter-multiple-text
        label="Place Published"
        :values.sync="hasPlacePublishedModel"
      />
      <reference-filter-multiple-text
        label="DOI"
        :values.sync="hasDoiModel"
      />
      <reference-filter-multiple-select
        label="Review Stage"
        :selected-values.sync="atReviewStageModel"
        :options="reviewStageOptions"
      />
      <reference-filter-screening-status
        :selected.sync="screeningStatusModel"
      />
      <reference-filter-full-text-review-status
        :selected.sync="fullTextReviewStatusModel"
        :has-full-text-pdf.sync="hasFullTextPDFModel"
      />
      <reference-filter-extraction-status
        :selected.sync="dataExtractionStatusModel"
      />
      <reference-filter-comments
        class="last-filter"
        :has-comments.sync="hasCommentsModel"
        :has-comments-by-you.sync="hasCommentsByYouModel"
      />
    </div>
  </div>
</template>

<script>
import ReferenceFiltersHeader from './ReferenceFiltersHeader';
import ReferenceTagFilter from './ReferenceTagFilter';
import ReferenceFilterMultipleText from './ReferenceFilterMultipleText';
import ReferenceFilterValueRange from './ReferenceFilterValueRange';
import ReferenceFilterMultipleSelect from './ReferenceFilterMultipleSelect';
import ReferenceFilterScreeningStatus from './ReferenceFilterScreeningStatus';
import ReferenceFilterFullTextReviewStatus from './ReferenceFilterFullTextReviewStatus';
import ReferenceFilterExtractionStatus from './ReferenceFilterExtractionStatus';
import ReferenceFilterComments from './ReferenceFilterComments';
import { ReferenceFilteringInputDto, Tag } from '@/models';
import { ReviewStage } from '@/enums';
import { typeHelpers } from '@/modules';
import { startCase, cloneDeep } from 'lodash';

const buildInputModelGetter = (key, castOp = (value) => value) => {
  return {
    get: function () {
      return this.filteringInput[key];
    },
    set: function (value) {
      const newFilteringInput = cloneDeep(this.filteringInput);
      this.$set(newFilteringInput, key, castOp(value));
      this.$emit('update:filteringInput', newFilteringInput);
    },
  };
};

const parseYear = (value) => {
  const parsedValue = parseInt(value, 10);
  return isNaN(parsedValue) ? null : parsedValue;
};

export default {
  name: 'ReferenceFilters',
  components: {
    ReferenceFiltersHeader,
    ReferenceTagFilter,
    ReferenceFilterMultipleText,
    ReferenceFilterValueRange,
    ReferenceFilterMultipleSelect,
    ReferenceFilterScreeningStatus,
    ReferenceFilterFullTextReviewStatus,
    ReferenceFilterExtractionStatus,
    ReferenceFilterComments,
  },
  props: {
    filteringInput: {
      type: ReferenceFilteringInputDto,
      required: true,
    },
    availableTags: {
      type: Array,
      default: () => [],
      validator: (val) => typeHelpers.isArrayOfType(val, Tag),
    },
  },
  emits: ['update:filteringInput'],
  data: () => ({
    reviewStageOptions: Object.values(ReviewStage).map((stage) => ({
      value: stage,
      label: startCase(stage.toLowerCase()),
    })),
  }),
  computed: {
    hasTagModel: buildInputModelGetter('hasTag'),
    hasKeywordModel: buildInputModelGetter('hasKeyword'),
    hasAuthorModel: buildInputModelGetter('hasAuthor'),
    hasPlacePublishedModel: buildInputModelGetter(
      'hasPlacePublished'
    ),
    hasDoiModel: buildInputModelGetter('hasDoi'),
    publishedAfterYearModel: buildInputModelGetter(
      'publishedAfterYear',
      parseYear
    ),
    publishedBeforeYearModel: buildInputModelGetter(
      'publishedBeforeYear',
      parseYear
    ),
    atReviewStageModel: buildInputModelGetter('atReviewStage'),
    screeningStatusModel: buildInputModelGetter('screeningStatus'),
    fullTextReviewStatusModel: buildInputModelGetter(
      'fullTextReviewStatus'
    ),
    hasFullTextPDFModel: buildInputModelGetter('hasFullTextPDF'),
    dataExtractionStatusModel: buildInputModelGetter(
      'dataExtractionStatus'
    ),
    hasCommentsModel: buildInputModelGetter('hasComments'),
    hasCommentsByYouModel: buildInputModelGetter('hasCommentsByYou'),
  },
  methods: {
    resetFilterInput() {
      this.$emit(
        'update:filteringInput',
        new ReferenceFilteringInputDto()
      );
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~vuetify/src/styles/styles.sass';

.reference-filters {
  background-color: white;
  border-top: 1px solid map-get($grey, 'lighten-2');
  border-right: 1px solid map-get($grey, 'lighten-2');

  // Make full height not including the header.
  height: calc(100vh - 117px);
  overflow-y: auto;

  .last-filter {
    border-bottom: 1px solid map-get($grey, 'lighten-2');
  }
}
</style>
